import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFirestoreQuery } from '../../firebase/useFirestore';
import { usePersonaPresenceMonth } from '../../hooks/usePersonaPresenceReports';
import useSettings from '../../hooks/Settings';
import { client } from '../../hooks/Client';
import Table from '../../components/common/table/Table.jsx';
import TableCell from '../../components/common/table/TableCell.jsx';
import computeBilling, { adjustmentBlocker } from '../../helpers/billing/computeBilling';
import resolveBillingRules from '../../helpers/billing/resolveBillingRules';
import { previousMonth, monthOptions, periodEndKey } from '../../helpers/billing/billingPeriod';
import { unitLabel, formatQuantity, skippedSummary, summarizeQuantities, groupSkippedByReason, adjustmentCountText } from '../../helpers/billing/billingLabels';
import formatDateKey from '../../helpers/billing/formatDateKey';
import skipFix from '../../helpers/billing/skipFix';
import PageHeader from '../../components/layout/PageHeader';
import buildBillingExportRows, { BILLING_EXPORT_HEADER } from '../../helpers/billing/buildBillingExportRows';
import downloaderFor from '../../helpers/export/downloaderFor';
import formatEuro from '../../helpers/formatEuro';
import BillingAdjustments from '../../components/billing/BillingAdjustments';

/**
 * Ondersteuning → Facturatie: per deelnemer wat er over een maand te factureren
 * valt, en wie er níet in staat en waarom.
 *
 * Er wordt pas gerekend als élke bron binnen is — instellingen, aanwezigheid,
 * deelnemers, trajecten, tarieven en extra regels. Zolang er één onderweg is staat er geen
 * bedrag en ook geen 'niets', want allebei zouden een bewering zijn die nog niet
 * waar hoeft te zijn. Zonder complete rekenregels rekent de pagina niets.
 *
 * AI-CONTEXT: See docs/features/facturatie-per-deelnemer.md
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 * AI-CONTEXT: See docs/pitfalls.md#ui-aggregate-collapses-nothing-into-zero
 *
 * @returns {JSX.Element}
 */
const Billing = () => {
    const settings = useSettings();
    const options = useMemo(() => monthOptions(new Date(), 24), []);
    const [selected, setSelected] = useState(() => {
        const { year, month } = previousMonth(new Date());
        return `${year}-${String(month).padStart(2, '0')}`;
    });
    const [year, month] = selected.split('-').map(Number);

    const [readErrors, setReadErrors] = useState(/** @type {Record<string, boolean>} */ ({}));
    /** @param {string} source */
    const onReadError = (source) => (error) => {
        console.error(`Billing: ${source} niet geladen`, error);
        setReadErrors((prev) => ({ ...prev, [source]: true }));
    };

    const presence = usePersonaPresenceMonth(month, year);
    const personas = useFirestoreQuery('Personas', { onError: onReadError('Personas') });
    const routes = useFirestoreQuery('PersonaRoutes', { onError: onReadError('PersonaRoutes') });
    const tariffs = useFirestoreQuery('PersonaTariffs', { onError: onReadError('PersonaTariffs') });
    const adjustments = useFirestoreQuery('PersonaBillingAdjustments', {
        where: [['Period', '==', selected]],
        onError: onReadError('PersonaBillingAdjustments'),
    });

    // Na een maandwissel geven beide maandbronnen één render lang nog de vorige
    // maand terug, als geldige array. Rekenen met die mix gaf een kort verkeerd
    // totaal (en een verkeerde export). De aanwezigheid die er op het moment van
    // de wissel was, geldt daarom niet als geladen tot er een nieuwe lijst is —
    // voor beide bronnen, want een lege lijst van de vorige maand ziet er precies
    // zo uit als een lege lijst van deze. De extra regels moeten daarnaast bij de
    // gekozen maand horen.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    const switchRef = useRef({ selected, staleReports: null, staleAdjustments: null });
    if (switchRef.current.selected !== selected) {
        switchRef.current = { selected, staleReports: presence.reports, staleAdjustments: adjustments };
    }
    const presenceLoaded = presence.loaded && presence.reports !== switchRef.current.staleReports;
    const adjustmentsLoaded = Array.isArray(adjustments)
        && adjustments !== switchRef.current.staleAdjustments
        && adjustments.every((a) => a.Period === selected);

    const { rules } = resolveBillingRules(settings?.billingRulesConfig);
    const hasError = !!presence.error || Object.values(readErrors).some(Boolean);
    const loaded = !!settings?.loaded && presenceLoaded && adjustmentsLoaded
        && Array.isArray(personas) && Array.isArray(routes) && Array.isArray(tariffs);

    const result = useMemo(() => {
        if (!loaded || !rules || hasError) return null;
        return computeBilling({
            reports: presence.reports,
            personas,
            routes,
            tariffs,
            adjustments,
            rules,
            periodEndKey: periodEndKey(year, month),
        });
        // `rules` is per render een nieuw object; de config is de echte bron.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loaded, hasError, settings?.billingRulesConfig, presence.reports, personas, routes, tariffs, adjustments, year, month]);

    /**
     * Waarom een extra regel voor deze deelnemer niet zou meetellen — dezelfde
     * regel als in de rekenkern, voor de melding onder het formulier.
     * @param {string} personaId
     */
    const blockerFor = (personaId) => {
        const persona = personas.find((p) => p.ID === personaId);
        const route = persona ? routes.find((r) => r.ID === persona.RouteID) : undefined;
        return adjustmentBlocker({
            persona,
            route,
            tariffs: tariffs.filter((t) => t.PersonaID === personaId),
            periodEndKey: periodEndKey(year, month),
        });
    };

    /**
     * Excel naast CSV, zoals bij Instellingen → Data: een CSV landt in een
     * Nederlandse Excel in één kolom en een bedrag met een punt wordt daar tekst.
     * In een Excel-bestand is een bedrag een getal.
     * @param {import('../../helpers/billing/computeBilling').BillingResult} billing
     * @param {'xlsx' | 'csv'} format
     */
    const exportBilling = (billing, format) => {
        downloaderFor(format)(buildBillingExportRows(billing), `Facturatie-${selected}.${format}`, BILLING_EXPORT_HEADER);
    };

    const hasContent = !!result && (result.lines.length > 0 || result.skipped.length > 0);

    const state = () => {
        if (hasError) {
            return <p className="billing-page__error" role="alert">De facturatie kon niet geladen worden. Herlaad de pagina; blijft het zo, vraag dan een beheerder.</p>;
        }
        if (!loaded) return <p className="billing-page__state">Facturatie wordt berekend…</p>;
        if (!rules) {
            return (
                <p className="billing-page__state">
                    De rekenregels zijn nog niet ingesteld. Stel ze in onder{' '}
                    <Link to={`/${client}/settings/6`}>Instellingen → Ondersteuning → Facturatie</Link>.
                </p>
            );
        }
        if (!hasContent) return <p className="billing-page__state">Geen aanwezigheid in deze maand.</p>;
        return null;
    };

    /**
     * Kerncijfers bovenaan. Het totaal draagt de zin over wie er niet in zit,
     * en het vlak 'Niet te berekenen' staat ernaast — een totaal mag nooit
     * compleet ogen terwijl er iemand mist.
     * AI-CONTEXT: See docs/pitfalls.md#ui-aggregate-collapses-nothing-into-zero
     */
    const tiles = () => {
        const computedPersonas = new Set(result.lines.map((l) => l.personaId)).size;
        return (
            <div className="billing-page__tiles">
                <div className="billing-page__tile" data-testid="billing-total">
                    <span className="billing-page__tile-label">Totaal {monthLabel}</span>
                    <span className="billing-page__tile-value">{formatEuro(result.total)}</span>
                    {result.skippedPersonaCount > 0 && <span className="billing-page__tile-note">{skippedSummary(result.skippedPersonaCount)}</span>}
                </div>
                <div className="billing-page__tile" data-testid="billing-computed">
                    <span className="billing-page__tile-label">Berekend</span>
                    <span className="billing-page__tile-value">
                        {computedPersonas} <small>{computedPersonas === 1 ? 'deelnemer' : 'deelnemers'}{result.lines.length > 0 && ` · ${summarizeQuantities(result.lines)}`}</small>
                    </span>
                </div>
                <div
                    className={`billing-page__tile${result.skippedPersonaCount > 0 ? ' billing-page__tile--warning' : ''}`}
                    data-testid="billing-skipped-count"
                >
                    <span className="billing-page__tile-label">Niet te berekenen</span>
                    <span className="billing-page__tile-value">
                        {result.skippedPersonaCount}{' '}
                        {result.skippedPersonaCount > 0
                            ? <a href="#billing-skipped"><small>{result.skippedPersonaCount === 1 ? 'deelnemer' : 'deelnemers'} ↓</small></a>
                            : <small>deelnemers</small>}
                    </span>
                </div>
            </div>
        );
    };

    /** Eén blok per deelnemer: de naam alleen op de eerste regel. */
    const isFirstOfPersona = (line, index) => index === 0 || result.lines[index - 1].personaId !== line.personaId;

    const lineLabel = (line, index) => {
        const first = isFirstOfPersona(line, index);
        const extra = line.kind === 'adjustment' && (
            <span className="billing-table__extra">
                <span className="billing-table__chip">Extra</span> {line.reason}
            </span>
        );
        if (first) return <>{line.name}{extra}</>;
        if (extra) return extra;
        // De ingangsdatum van het tarief, niet de eerste aanwezige dag: dat is wat
        // verklaart waarom deze regel een ander tarief heeft.
        return (
            <span className="billing-table__sub">
                {line.tariffStartDate ? `tarief vanaf ${formatDateKey(line.tariffStartDate).slice(0, 5)}` : 'tarief van traject'}
            </span>
        );
    };

    const units = new Set(result?.lines.map((l) => l.unit));
    const presenceDays = result ? result.lines.reduce((sum, l) => sum + (l.days || 0), 0) : 0;
    const quantityTotal = result ? result.lines.reduce((sum, l) => sum + l.quantity, 0) : 0;

    const table = () => (
        <Table
            columns={[
                { key: 'name', label: 'DEELNEMER', searchable: false },
                { key: 'route', label: 'TRAJECT', searchable: false },
                { key: 'days', label: 'DAGEN', searchable: false, headerClassName: 'billing-table__num' },
                { key: 'quantity', label: 'AANTAL', searchable: false, headerClassName: 'billing-table__num' },
                { key: 'rate', label: 'TARIEF', searchable: false, headerClassName: 'billing-table__num' },
                { key: 'amount', label: 'BEDRAG', searchable: false, headerClassName: 'billing-table__num' },
            ]}
            items={result.lines}
            getItemKey={(line) => `${line.personaId}|${line.kind}|${line.adjustmentId || ''}|${line.tariffSource}|${line.rate}`}
            getRowClassName={(line, index) => (isFirstOfPersona(line, index) ? '' : 'billing-table__row--continued')}
            showSearch={false}
            tableClassName="billing-table"
            renderRow={(line, index) => (
                <>
                    <TableCell>{lineLabel(line, index)}</TableCell>
                    <TableCell>{isFirstOfPersona(line, index) && `${line.routeTitle} · ${unitLabel(line.unit).toLowerCase()}`}</TableCell>
                    <TableCell className="billing-table__num">{line.kind === 'presence' ? line.days : '–'}</TableCell>
                    <TableCell className="billing-table__num">{formatQuantity(line.quantity)}</TableCell>
                    <TableCell className="billing-table__num">
                        {formatEuro(line.rate)}
                        {/* Op een vervolgregel staat het al in de eerste kolom. */}
                        {line.tariffSource === 'route' && isFirstOfPersona(line, index) && <span className="billing-table__sub">tarief van traject</span>}
                    </TableCell>
                    <TableCell className="billing-table__num">{formatEuro(line.amount)}</TableCell>
                </>
            )}
            footerRow={(
                <>
                    <TableCell>Totaal</TableCell>
                    <TableCell />
                    <TableCell className="billing-table__num">{presenceDays}</TableCell>
                    {/* Alleen optellen binnen één eenheid: 4 dagdelen en 3 uur zijn geen 7. */}
                    <TableCell className="billing-table__num">{units.size === 1 ? formatQuantity(quantityTotal) : ''}</TableCell>
                    <TableCell />
                    <TableCell className="billing-table__num">{formatEuro(result.total)}</TableCell>
                </>
            )}
        />
    );

    /** Waar je dit oplost, als er iets op te lossen valt. */
    const fixLink = (reason, personaId) => {
        const fix = skipFix(reason, personaId);
        return fix && <Link className="billing-skipped__fix" to={`/${client}/${fix.path}`}>{fix.label}</Link>;
    };

    const skippedCard = () => (
        <section className="billing-page__card" id="billing-skipped" aria-labelledby="billing-skipped-title">
            <h3 id="billing-skipped-title">Niet te berekenen <span className="billing-page__count">· {result.skippedPersonaCount}</span></h3>
            {result.skipped.length === 0 ? (
                <p className="billing-page__state">Iedereen met aanwezigheid in deze maand is berekend.</p>
            ) : groupSkippedByReason(result.skipped).map((group) => (
                <div key={group.reason} className="billing-skipped__group">
                    <h4 className="billing-skipped__reason">{group.label}</h4>
                    <ul>
                        {group.entries.map((entry) => (
                            <li key={`${entry.personaId}|${entry.reason}`}>
                                <span className="billing-skipped__name">{entry.name || 'Onbekende deelnemer'}</span>
                                {entry.detail && <span className="billing-skipped__meta">({unitLabel(entry.detail)})</span>}
                                {entry.dates.length > 0 && (
                                    <details className="billing-skipped__dates">
                                        <summary>{entry.dates.length} {entry.dates.length === 1 ? 'dag' : 'dagen'}</summary>
                                        {entry.dates.map(formatDateKey).join(', ')}
                                    </details>
                                )}
                                {entry.adjustmentIds.length > 0 && (
                                    <span className="billing-skipped__meta">· {adjustmentCountText(entry.adjustmentIds.length)}</span>
                                )}
                                {fixLink(entry.reason, entry.personaId)}
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
        </section>
    );

    const monthLabel = options.find((o) => o.value === selected)?.label.split(' ')[0] || '';

    return (
        <div className="main">
            <div className="main-container">
                <PageHeader
                    heading="Facturatie"
                    description="Wat je per deelnemer factureert, per maand."
                    AI={false}
                    actions={(
                        <>
                            <select className="billing-page__month" aria-label="Maand" value={selected} onChange={(e) => setSelected(e.target.value)}>
                                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                            {/* Alleen als er een uitkomst is: de export erft zo dezelfde laad- en
                                regelpoorten als het scherm, en krijgt dezelfde uitkomst.
                                AI-CONTEXT: See docs/pitfalls.md#ui-export-claims-everything-on-screen */}
                            {hasContent && (
                                <>
                                    <button type="button" className="page-header-button" onClick={() => exportBilling(result, 'xlsx')}>
                                        Download als Excel
                                    </button>
                                    <button type="button" className="page-header-button" onClick={() => exportBilling(result, 'csv')}>
                                        Download als CSV
                                    </button>
                                </>
                            )}
                        </>
                    )}
                />
                <div className="billing-page">
                    {state()}
                    {hasContent && (
                        <>
                            {tiles()}
                            {result.lines.length > 0 && table()}
                        </>
                    )}
                    {loaded && rules && !hasError && (
                        <div className="billing-page__cards">
                            {hasContent && skippedCard()}
                            <BillingAdjustments
                                period={selected}
                                personas={personas}
                                adjustments={adjustments}
                                notCounted={result.skippedAdjustments}
                                blockerFor={blockerFor}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Billing;
